import { useEffect, useState } from 'react';
import {
  SidePanel,
  SidePanelType,
  IconDownload,
  formatDate,
  IconPaper,
  Checkbox,
  SidePanelLoader,
  ButtonSecondary,
  ButtonSize,
  usePrevious,
  IconArrowRight,
  ClickOutsideDetector,
  ButtonPrimary,
  ButtonPseudo,
  IconLoader,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetChannelDocumentsQuery,
  useLazyGetChatDocumentFileQuery,
  useMoveDocumentsToCustomerMutation,
} from '../hoc/store/services';
import { setDocuments } from '../hoc/store/slices/message';
import {
  ChannelType,
  CustomerDocumentType,
  CustomerType,
  Document,
} from '../hoc/types';
import { AttachmentSidepanelTypes } from './types';

import cn from './messages.module.css';

const getDocumentCategory = (category: CustomerDocumentType) => {
  let categoryName;

  switch (category) {
    case CustomerDocumentType.CLIENT_IDENTIFICATION:
      categoryName = 'Identification documents';
      break;
    case CustomerDocumentType.CLIENT_TRANSACTION:
      categoryName = 'Transaction documents';
      break;
    case CustomerDocumentType.CLIENT_PSP_CUSTOMER:
      categoryName = 'PSP-Customer documents';
      break;
    case CustomerDocumentType.CLIENT_CDD_CHECK:
      categoryName = 'CDD check documents';
      break;
    case CustomerDocumentType.OTHER:
      categoryName = 'Other';
      break;
    default:
      categoryName = category;
      break;
  }

  return categoryName;
};

const AttachmentSidepanel = ({
  panelIsOpen,
  setPanelIsOpen,
  panelState,
  setCloseChatPopupVisible,
  messageId,
  selectedDocumentIds,
  setSelectedDocumentIds,
  organizationType,
  channelType,
  organizationId,
}: AttachmentSidepanelTypes) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [selectedDocumentCategory, setSelectedDocumentCategory] = useState('');
  const [selectedDocumentId, setSelectedDocumentId] = useState('');

  const prevIsOpen = usePrevious(panelIsOpen);

  let content;

  const { documents } = useAppSelector((state) => state.messages.message);

  const dispatch = useAppDispatch();

  const [getChannelsDocuments, { isLoading, isFetching }] =
    useLazyGetChannelDocumentsQuery();
  const [
    getChatFile,
    { isLoading: chatFileIsLoading, isFetching: chatFileIsFetching },
  ] = useLazyGetChatDocumentFileQuery();
  const [
    downloadFile,
    { isLoading: downloadInProgress, isFetching: downloadIsFetching },
  ] = useLazyGetChatDocumentFileQuery();

  const [
    onMoveDocuments,
    { isLoading: moveIsLoading, isSuccess: moveIsSuccess },
  ] = useMoveDocumentsToCustomerMutation();

  const onMove = (category: CustomerDocumentType) => {
    setSelectedDocumentCategory(category);

    onMoveDocuments({
      id: messageId as string,
      body: {
        ids: selectedDocumentIds,
        category,
      },
    });
  };

  const onFileDownload = async (id: string) => {
    const response = await downloadFile({
      id: messageId as string,
      documentId: id,
    });

    if (response && response.data) {
      setSelectedDocumentId('');

      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      const documentName = documents?.find((item) => item.id === id)?.name;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = documentName as string;
      hiddenElement.click();
    }
  };

  const getFiles = async () => {
    if (selectedDocumentIds && selectedDocumentIds.length) {
      const downloadedFiles = await Promise.all(
        selectedDocumentIds.map(async (item) => {
          const response = await getChatFile({
            id: messageId as string,
            documentId: item,
          });

          return response.data;
        }),
      );

      if (downloadedFiles) {
        downloadedFiles.forEach((file, index) => {
          const hiddenElement = document.createElement('a');
          const url = window.URL || window.webkitURL;

          const documentName = documents?.find(
            (item) => item.id === selectedDocumentIds[index],
          )?.name;

          // @ts-ignore
          const blob = url.createObjectURL(file);
          hiddenElement.href = blob;
          hiddenElement.target = '_blank';
          hiddenElement.download = documentName as string;
          hiddenElement.click();
        });
      }
    }
  };

  useEffect(() => {
    if (panelIsOpen && !documents) {
      getChannelsDocuments(messageId as string);
    }
  }, [panelIsOpen]);

  useEffect(() => {
    if (moveIsSuccess && organizationId && documents) {
      const nextDocuments = documents?.map((item) => {
        const result = { ...item };

        if (selectedDocumentIds?.find((id) => id === result.id)) {
          result.organizationId = organizationId;
          result.category = selectedDocumentCategory;
        }

        return result;
      });

      dispatch(setDocuments(nextDocuments as Document[]));

      setSelectedDocumentIds([]);
      setSelectedDocumentCategory('');
    }
  }, [moveIsSuccess, selectedDocumentCategory]);

  useEffect(() => {
    if (!panelIsOpen && prevIsOpen) {
      setSelectedDocumentIds([]);
    }
  }, [panelIsOpen, prevIsOpen]);

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (documents?.length) {
    content = (
      <div className={cn.attacmentsWrapper}>
        {panelState === 'closing' && (
          <div className={cn.attachmentsPanelText}>
            Select the attachments you want to attach to the customer profile
            before closing the chat.
          </div>
        )}
        <div className={cn.attacmentsControls}>
          {(channelType === ChannelType.MAIN ||
            channelType === ChannelType.TRANSACTION) && (
            <ClickOutsideDetector setOptionsListVisible={setMenuVisible}>
              <>
                <ButtonSecondary
                  text={moveIsLoading ? 'Loading...' : 'Move to Customer'}
                  icon={<IconArrowRight />}
                  disabled={!selectedDocumentIds.length || moveIsLoading}
                  className={`button-white ${cn.moveToCustomerButton}`}
                  size={ButtonSize.SMALL}
                  onClick={() => setMenuVisible(!menuVisible)}
                />
                {menuVisible && (
                  <ul className={cn.selectedDocumentsMenu}>
                    <li className={cn.selectedDocumentsMenuTitle}>
                      {`Which group to move ${selectedDocumentIds?.length} documents to?`}
                    </li>
                    {organizationType !== CustomerType.CORPORATE && (
                      <li>
                        <button
                          type="button"
                          className={cn.selectedDocumentsMenuButton}
                          onClick={() => {
                            setMenuVisible(false);
                            onMove(CustomerDocumentType.CLIENT_IDENTIFICATION);
                          }}
                          tabIndex={-1}
                        >
                          Identification documents
                          <div>
                            Move <IconArrowRight />
                          </div>
                        </button>
                      </li>
                    )}
                    <li>
                      <button
                        type="button"
                        className={cn.selectedDocumentsMenuButton}
                        onClick={() => {
                          setMenuVisible(false);
                          onMove(CustomerDocumentType.CLIENT_TRANSACTION);
                        }}
                        tabIndex={-1}
                      >
                        Transaction documents
                        <div>
                          Move <IconArrowRight />
                        </div>
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className={cn.selectedDocumentsMenuButton}
                        onClick={() => {
                          setMenuVisible(false);
                          onMove(CustomerDocumentType.CLIENT_PSP_CUSTOMER);
                        }}
                        tabIndex={-1}
                      >
                        PSP-Customer documents
                        <div>
                          Move <IconArrowRight />
                        </div>
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className={cn.selectedDocumentsMenuButton}
                        onClick={() => {
                          setMenuVisible(false);
                          onMove(CustomerDocumentType.CLIENT_CDD_CHECK);
                        }}
                        tabIndex={-1}
                      >
                        CDD check documents
                        <div>
                          Move <IconArrowRight />
                        </div>
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className={cn.selectedDocumentsMenuButton}
                        onClick={() => {
                          setMenuVisible(false);
                          onMove(CustomerDocumentType.OTHER);
                        }}
                        tabIndex={-1}
                      >
                        Other documents
                        <div>
                          Move <IconArrowRight />
                        </div>
                      </button>
                    </li>
                  </ul>
                )}
              </>
            </ClickOutsideDetector>
          )}

          <ButtonSecondary
            text={
              chatFileIsLoading || chatFileIsFetching
                ? 'Loading...'
                : 'Downloads'
            }
            icon={
              chatFileIsLoading || chatFileIsFetching ? undefined : (
                <IconDownload />
              )
            }
            size={ButtonSize.SMALL}
            disabled={!selectedDocumentIds.length}
            className="button-white"
            onClick={getFiles}
          />
        </div>
        <div className={cn.attachmentsList}>
          <div className={cn.attachemtsListTop}>
            <Checkbox
              title="Select all"
              type="checkbox"
              name="select-all"
              checked={
                selectedDocumentIds?.length ===
                documents.filter((item) => !item.organizationId).length
              }
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedDocumentIds(
                    documents
                      .filter((item) => !item.organizationId)
                      .map((item) => item.id),
                  );
                } else {
                  setSelectedDocumentIds([]);
                }
              }}
            />
          </div>
          {documents.map((item) => (
            <div className={cn.documentItem} key={item.id}>
              <Checkbox
                labelClassName={`${cn.documentCheckboxWrapper} ${
                  item.organizationId ? cn.disabled : ''
                }`}
                name={`document-${item.id}`}
                title=""
                type="checkbox"
                checked={
                  selectedDocumentIds.includes(item.id) ||
                  Boolean(item.organizationId)
                }
                disabled={Boolean(item.organizationId)}
                visualClassName={cn.documentCheckbox}
                onChange={(e) => {
                  let nextSelectedDocumentsIds = ([] as string[]).concat(
                    selectedDocumentIds,
                  );

                  if (e.target.checked) {
                    nextSelectedDocumentsIds.push(item.id);
                  } else {
                    nextSelectedDocumentsIds = nextSelectedDocumentsIds.filter(
                      (id) => item.id !== id,
                    );
                  }

                  setSelectedDocumentIds(nextSelectedDocumentsIds);
                }}
              />
              <div className={cn.documentItemInfo}>
                <div
                  className={`${cn.documentItemInfoTop} ${
                    item.organizationId ? cn.greyColor : ''
                  }`}
                >
                  <IconPaper className={cn.iconPaper} />
                  {item.name}
                </div>
                <div className={cn.documentDate}>
                  {formatDate(item.createdAt, true)}
                </div>
                {item.organizationId && item.category && (
                  <div
                    className={cn.documentCategory}
                  >{`In documents: ${getDocumentCategory(
                    item.category as CustomerDocumentType,
                  )}`}</div>
                )}
                <button
                  type="button"
                  aria-label="Download button"
                  className={cn.documentDownloadButton}
                  onClick={() => {
                    onFileDownload(item.id);
                    setSelectedDocumentId(item.id);
                  }}
                >
                  {selectedDocumentId === item.id &&
                  (downloadInProgress || downloadIsFetching) ? (
                    <IconLoader width={16} height={16} />
                  ) : (
                    <IconDownload />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  } else {
    content = <div className={cn.emptyAttachments}>No attachments yet</div>;
  }

  return (
    <SidePanel
      type={SidePanelType.AML}
      closeOnClickOutside
      isOpen={panelIsOpen}
      onClose={() => setPanelIsOpen(false)}
      title="Attachments"
      sidepanelExpandedKey="attachments-panel-messages"
      footer={
        panelState === 'closing' ? (
          <>
            <ButtonPrimary
              text="Close chat"
              onClick={() => setCloseChatPopupVisible(true)}
              bold
            />
            <ButtonPseudo
              text="Cancel"
              onClick={() => setPanelIsOpen(false)}
              bold
            />
          </>
        ) : undefined
      }
    >
      {content}
    </SidePanel>
  );
};

export default AttachmentSidepanel;
